<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import DRBtn from "../UI/DRBtn.vue";
import DRBtnLink from "../UI/DRBtnLink.vue";
import { usePwaController } from "@/composable/usePWA";
import $ls, { LocalStorageKey } from "@/utils/local-storage";

const HIDE_ALERT_DURATION = 5 * 1000; //  24 * 60 * 60 * 1000; // 24h

const {
  needRefresh,
  reload,
  shouldChangeBrowserAlert,
  isDesktop,
  isMobileOrTablet,
} = usePwaController();

const route = useRoute();

// Hide browser/install alerts on field-observations route or on mobile
const isFieldObservationsRoute = computed(
  () => route.name === "field-observations"
);
const shouldHideAlerts = computed(
  () => isFieldObservationsRoute.value || isMobileOrTablet()
);

const showAlert = (
  showAlert: boolean,
  dontShowAgain: boolean,
  lastCloseDate: Date | null
) => {
  if (dontShowAgain || !showAlert) return false;
  if (!lastCloseDate) return true;
  return new Date().getTime() - lastCloseDate.getTime() > HIDE_ALERT_DURATION;
};

//BROWSER ALERT
const dontShowBrowserAlert = ref(
  $ls.getBooleanItem(LocalStorageKey.DONT_SHOW_BROWSER_ALERT)
);

const lastCloseDateBrowserAlert = ref(
  $ls.getDateItem(LocalStorageKey.BROWSER_ALERT_CLOSE_DATE)
);

const handleDontShowBrowserAlert = () => {
  shouldChangeBrowserAlert.value = false;
  $ls.setBooleanItem(LocalStorageKey.DONT_SHOW_BROWSER_ALERT, true);
  dontShowBrowserAlert.value = true;
};

const handleCloseBrowserAlert = () => {
  shouldChangeBrowserAlert.value = false;
  const now = new Date();
  $ls.setDateItem(LocalStorageKey.BROWSER_ALERT_CLOSE_DATE, now);
  lastCloseDateBrowserAlert.value = now;
};
</script>

<template>
  <div v-if="needRefresh" class="overlay">
    <div class="popup">
      <div class="text-center h4 pb-2">Mise à jour disponible</div>
      <span>
        Une nouvelle version est disponible. Pour continuer, vous pouvez
        recharger la page pour mettre à jour l'application.
      </span>
      <div class="buttons">
        <DRBtn label="Recharger" @click="reload" />
      </div>
    </div>
  </div>

  <!-- SUGGEST BROWSER ALERT -->
  <div
    v-else-if="
      !shouldHideAlerts &&
      showAlert(
        shouldChangeBrowserAlert,
        dontShowBrowserAlert,
        lastCloseDateBrowserAlert
      )
    "
    class="overlay"
  >
    <div class="popup">
      <div class="text-center h4 pb-2">{{ $t("browerModal.title") }}</div>
      <span>
        {{ $t("browerModal.message") }}
      </span>
      <div
        class="buttons"
        :style="{ justifyContent: isDesktop ? 'space-between' : 'center' }"
      >
        <DRBtnLink
          v-if="isDesktop"
          :label="$t('dontShowAgain')"
          @click="handleDontShowBrowserAlert"
        ></DRBtnLink>
        <DRBtn :label="$t('understood')" @click="handleCloseBrowserAlert" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5); /* Fond semi-transparent */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* Pour s'assurer que la popup soit par-dessus tout */
}

.popup {
  background: var(--bs-white);
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 500px;
  width: 90%;
}

.buttons {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.buttons button {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.fixed-badge {
  position: fixed;
  bottom: 10px;
  right: 10px;
  background: var(--bs-danger);
  color: white;
  padding: 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}
</style>
